import {
  type BEFinSessionResponse,
  getBEFinSession,
} from 'api/v2/users/beFinances';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { TaxesValidationCacheKeys } from 'types/cacheKeys.types';
import { Connector } from 'types/transactions.types';
import { AvailableCountries, IUser, VATTypeEnum } from 'types/users.types';

const useIsPeppolPreVerified = () => {
  const userData = useGetCurrentCustomerData().data as IUser | undefined;
  const connectorsData = useGetConnectors().data as Connector[] | undefined;
  const minfinSessionsData = useQuery(
    TaxesValidationCacheKeys.MinFinSessionStatus,
    () => getBEFinSession(),
  ).data as BEFinSessionResponse | undefined;

  return useMemo(() => {
    const shouldUseIban =
      userData?.country === AvailableCountries.germany &&
      userData?.VATType !== VATTypeEnum.subjectToVAT;

    const user = {
      name: `${userData?.firstName} ${userData?.lastName}`,
      IBAN: userData?.IBAN,
      VATNumber: userData?.VATNumber,
    };

    const activeConnectors =
      connectorsData?.filter((connector) => connector.status === 'ready') || [];

    if (shouldUseIban) {
      return activeConnectors.some((connector) => {
        const bankAccountHolder =
          connector.bankAccountReference.accountHolderName;

        const bankAccountIBAN = connector.bankAccountReference.IBAN;

        return bankAccountHolder === user.name && bankAccountIBAN === user.IBAN;
      });
    }

    const usesMinfinVATNumber =
      !!minfinSessionsData?.sessionVATNumber &&
      minfinSessionsData?.sessionVATNumber === user.VATNumber;

    const usesConnectorData = activeConnectors.some((connector) => {
      return connector.bankAccountReference.accountHolderName === user.name;
    });

    return usesMinfinVATNumber || usesConnectorData;
  }, [userData, connectorsData, minfinSessionsData]);
};

export default useIsPeppolPreVerified;
